/**
 * DomainSettingsDialog — Sheet Migration Wave 4b (AC-W4b-04)
 *
 * Renders as a right-slide FormSheet (md:max-w-3xl). Edit-only (settings for
 * an existing domain — no create mode, no URL routing).
 *
 * 7 inputs (PW6 AC-Domains-2: added Accountable):
 *   0. accountableActorId     (AccountablePicker, actor-only)
 *   1. adoptionLevel          (Select)
 *   2. isAdvisory             (Switch)
 *   3. wipInProgress          (Input number)
 *   4. wipQualityConfirmation (Input number)
 *   5. wipBlocked             (Input number)
 *   6. wipEscalated           (Input number)
 *
 * Prior implementation used Modal — replaced with FormSheet + useFormSnapshot
 * for dirty-state guard, discard banner, and sticky header (W2.5 hooks).
 *
 * Async-seed pattern: depKey = settingsQuery.dataUpdatedAt so useFormSnapshot
 * reseeds both `state` and `baseline` when settings arrive from the server,
 * keeping the form clean (not dirty) after load.
 *
 * PW6 AC-Domains-2: header section shows domain icon + name + description
 * (read-only). Accountable field added with dirty-tracking + Save wired to
 * the existing setRorosDomainAccountable mutation.
 */

import React, { useMemo } from 'react';
import { FormSheet } from '@/components/ui/FormSheet';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { showToast } from '@/components/ui/Toast';
import { AccountablePicker } from '@/components/roros/AccountablePicker';
import {
  useRorosDomainSettings,
  useRorosDomainUpdateSettings,
  useRorosDomainSetAccountable,
} from '@/hooks/roros/domains';
import { useRorosActors } from '@/hooks/roros/actors';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { RorosAdoptionLevel } from '@/types/roros/enums';
import { LoadingState } from '@/components/ui/LoadingState';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';
import { DOMAIN_LIST } from '@/routes/roros/launchpad/types';
import { Globe } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { t } from '@/lib/i18n';

interface DomainSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  domain: RorosDomainDTO;
}

// ─── Domain icon helper ────────────────────────────────────────────────────────

function getDomainIcon(domainId: number) {
  const domainInfo = DOMAIN_LIST.find((d) => d.domain_id === domainId);
  if (!domainInfo) return <Globe className="h-5 w-5" />;
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[domainInfo.icon];
  return IconComponent ? <IconComponent className="h-5 w-5" /> : <Globe className="h-5 w-5" />;
}

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type DomainSettingsSnapshot = {
  accountableActorId: string | null;
  adoptionLevel: string;
  isAdvisory: boolean;
  wipInProgress: number;
  wipQualityConfirmation: number;
  wipBlocked: number;
  wipEscalated: number;
};

function diffDomainSettingsSnapshots(
  base: DomainSettingsSnapshot,
  cur: DomainSettingsSnapshot,
): Partial<Record<keyof DomainSettingsSnapshot, boolean>> {
  return {
    accountableActorId: base.accountableActorId !== cur.accountableActorId,
    adoptionLevel: base.adoptionLevel !== cur.adoptionLevel,
    isAdvisory: base.isAdvisory !== cur.isAdvisory,
    wipInProgress: base.wipInProgress !== cur.wipInProgress,
    wipQualityConfirmation: base.wipQualityConfirmation !== cur.wipQualityConfirmation,
    wipBlocked: base.wipBlocked !== cur.wipBlocked,
    wipEscalated: base.wipEscalated !== cur.wipEscalated,
  };
}

export function DomainSettingsDialog({ isOpen, onClose, domain }: DomainSettingsDialogProps) {
  const settingsQuery = useRorosDomainSettings(domain.id);
  const updateMutation = useRorosDomainUpdateSettings();
  const setAccountableMutation = useRorosDomainSetAccountable();
  const actorsQuery = useRorosActors();

  const actors = useMemo<RorosActorDTO[]>(() => {
    const raw = actorsQuery.data as RorosActorDTO[] | undefined;
    return raw ?? [];
  }, [actorsQuery.data]);

  // Build adoption options inline so t() is called at render time (not module init).
  const ADOPTION_OPTIONS = [
    { value: RorosAdoptionLevel.SHADOW, label: t('Shadow') },
    { value: RorosAdoptionLevel.COPILOT, label: t('Copilot') },
    { value: RorosAdoptionLevel.AUTOPILOT, label: t('Autopilot') },
  ];

  // ─── Form state via useFormSnapshot ──────────────────────────────────────
  //
  // depKey = dataUpdatedAt (a number) so useFormSnapshot reseeds from the
  // latest factory closure whenever React Query delivers fresh settings data.
  // Both `state` and `baseline` are updated, keeping the form clean.
  const { state, setField, baseline, current } = useFormSnapshot<DomainSettingsSnapshot>({
    initialFactory: () => ({
      accountableActorId: domain.accountableActorId ?? null,
      adoptionLevel: settingsQuery.data?.defaultAdoptionLevel ?? RorosAdoptionLevel.SHADOW,
      isAdvisory: settingsQuery.data?.isAdvisory ?? false,
      wipInProgress: settingsQuery.data?.wipLimits?.in_progress ?? 0,
      wipQualityConfirmation: settingsQuery.data?.wipLimits?.quality_confirmation ?? 0,
      wipBlocked: settingsQuery.data?.wipLimits?.blocked ?? 0,
      wipEscalated: settingsQuery.data?.wipLimits?.escalated ?? 0,
    }),
    isOpen,
    // Re-seed when data arrives (dataUpdatedAt is 0 before first fetch).
    depKey: settingsQuery.dataUpdatedAt,
  });

  const {
    accountableActorId,
    adoptionLevel,
    isAdvisory,
    wipInProgress,
    wipQualityConfirmation,
    wipBlocked,
    wipEscalated,
  } = state;

  /**
   * Save handler — sequenced mutations (F-04 fix).
   *
   * 1. If accountable changed: await setRorosDomainAccountable first.
   *    On error → show toast, leave sheet open so the user can retry.
   *    Do NOT proceed to the settings mutation.
   * 2. Await updateDomainSettings.
   *    On error → show toast, leave sheet open.
   * 3. On full success → show success toast and close.
   *
   * Sequential ordering guarantees:
   *  - No double-write race (the two network calls never overlap).
   *  - Partial success (accountable saved, settings failed) leaves the form
   *    open with only the settings fields dirty on retry, because:
   *      - setRorosDomainAccountable.onSuccess invalidates domains.settings
   *        which advances settingsQuery.dataUpdatedAt (depKey) → useFormSnapshot
   *        reseeds with the updated accountableActorId from props.
   */
  const handleSave = async () => {
    // Step 1: accountable (if changed).
    if (accountableActorId !== baseline.accountableActorId) {
      try {
        await setAccountableMutation.mutateAsync({ domainId: domain.id, actorId: accountableActorId });
      } catch (err) {
        showToast.error(err instanceof Error ? err.message : t('Failed to update accountable'));
        // Leave sheet open — user can retry both fields.
        return;
      }
    }

    // Step 2: remaining settings.
    try {
      await updateMutation.mutateAsync({
        domainId: domain.id,
        payload: {
          default_adoption_level: adoptionLevel,
          wip_limits: {
            in_progress: wipInProgress,
            quality_confirmation: wipQualityConfirmation,
            blocked: wipBlocked,
            escalated: wipEscalated,
          },
          is_advisory: isAdvisory,
        },
      });
      showToast.success(t('Domain settings updated successfully'));
      onClose();
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to update settings'));
      // Leave sheet open — accountable already saved; retry will only re-send settings.
    }
  };

  const handleWipChange = (key: keyof DomainSettingsSnapshot, value: string) => {
    const num = parseInt(value, 10);
    setField(key, isNaN(num) ? 0 : num);
  };

  const isSubmitting = updateMutation.isPending || setAccountableMutation.isPending;

  return (
    <FormSheet<DomainSettingsSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('Settings')}: ${domain.name}`}
      mode="edit"
      baseline={baseline}
      current={current}
      diff={diffDomainSettingsSnapshots}
      onSubmit={handleSave}
      isSubmitting={isSubmitting}
      submitLabel={t('Save Settings')}
      submittingLabel={t('Saving…')}
      submitTestId="domain-settings-save-button"
      width="md"
    >
      {settingsQuery.isLoading ? (
        <LoadingState text={t('Loading settings...')} />
      ) : (
        <div className="space-y-6">
          {/* AC-Domains-2: domain header — icon + name + description (read-only) */}
          <div className="flex items-start gap-3 pb-4 border-b border-border">
            <div className="p-2 rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
              {getDomainIcon(domain.domainId)}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-foreground">{domain.name}</h3>
              {domain.description && (
                <p data-testid="domain-settings-description" className="text-sm text-muted-foreground mt-1">{domain.description}</p>
              )}
            </div>
          </div>

          {/* AC-Domains-2: Accountable field (editable, actor-only) */}
          <div>
            <div className="text-sm font-medium mb-1">{t('Accountable')}</div>
            <AccountablePicker
              value={{ actorId: accountableActorId, domainId: null }}
              onChange={({ actorId }) => setField('accountableActorId', actorId)}
              actors={actors}
              mode="actor-only"
              disabled={isSubmitting}
              placeholder={t('None')}
              data-testid="domain-settings-accountable-picker"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('The actor responsible for this domain.')}
            </p>
          </div>

          {/* 1. Default Adoption Level */}
          <div>
            <Select
              label={t('Default Adoption Level')}
              options={ADOPTION_OPTIONS}
              value={adoptionLevel}
              onValueChange={(v) => setField('adoptionLevel', v)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('The default adoption level for new AI operators assigned to this domain.')}
            </p>
          </div>

          {/* 2. Advisory toggle */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t('Advisory')}</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("Advisory domains don't enforce WIP limits or appear on Control Tower lanes.")}
                </p>
              </div>
              <Switch
                checked={isAdvisory}
                onChange={(e) => setField('isAdvisory', e.target.checked)}
              />
            </div>
          </div>

          {/* 3–6. WIP Limits */}
          <div>
            <h4 className="text-sm font-medium mb-3">{t('WIP Limits')}</h4>
            <p className="text-xs text-muted-foreground mb-3">
              {t('Set limits for maximum concurrent work items per status. Use 0 for no limit.')}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('In Progress')}
                type="number"
                min={0}
                value={wipInProgress}
                onChange={(e) => handleWipChange('wipInProgress', e.target.value)}
              />
              <Input
                label={t('Quality Confirmation')}
                type="number"
                min={0}
                value={wipQualityConfirmation}
                onChange={(e) => handleWipChange('wipQualityConfirmation', e.target.value)}
              />
              <Input
                label={t('Blocked')}
                type="number"
                min={0}
                value={wipBlocked}
                onChange={(e) => handleWipChange('wipBlocked', e.target.value)}
              />
              <Input
                label={t('Escalated')}
                type="number"
                min={0}
                value={wipEscalated}
                onChange={(e) => handleWipChange('wipEscalated', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </FormSheet>
  );
}
